import { callApi } from './client';

/**
 * Voucher redemption + auth + cart endpoints used by the redemption flow.
 *
 * The cart is session-bound on the backend, so the order of calls matters:
 *   1. addVoucherCode populates the cart at $0 (no auth needed)
 *   2. login or create gives the session a user
 *   3. processTransaction finalises — assigns the course to the user
 */

export interface CartLineItem {
  productId?: number;
  productName?: string;
  price?: number;
  [key: string]: unknown;
}

export interface CartDisplayValues {
  lineItems?: CartLineItem[];
  totalPrice?: number | string;
  totalCount?: number;
  [key: string]: unknown;
}

/**
 * Apply a voucher code to the current session's cart. On success the
 * cart contains line items for the products the voucher unlocks, all
 * priced at $0. Throws ApiError if the code is invalid / expired /
 * already redeemed.
 */
export async function addVoucherCode(code: string): Promise<CartDisplayValues> {
  return callApi<CartDisplayValues>('cartApi', 'addVoucherCode', {
    voucherCode: code,
  });
}

/** Empty out the cart — used when the user wants to re-enter a code. */
export async function emptyCart(): Promise<unknown> {
  return callApi('cartApi', 'emptyCart');
}

/**
 * Total price helper — used to confirm the cart is $0 before we try to
 * checkout without payment info.
 */
export async function totalPrice(): Promise<number> {
  const r = await callApi<number | string>('cartApi', 'totalPrice');
  const n = typeof r === 'string' ? parseFloat(r) : r;
  return Number.isFinite(n) ? Number(n) : 0;
}

/**
 * Finalise the cart — issues whatever the line items grant. For a
 * pure-voucher cart the total is 0 and no payment info is needed.
 */
export async function processTransaction(): Promise<unknown> {
  return callApi('cartApi', 'processTransaction', {});
}

// ---- Auth (userApi) -------------------------------------------------

export interface UserInfo {
  userId: number;
  userName: string;
  fName?: string;
  lName?: string;
  emailAddress?: string;
}

export async function login(userName: string, password: string): Promise<boolean> {
  const r = await callApi<{ authenticated?: boolean } | boolean>(
    'userApi',
    'login',
    { userName, password },
  );
  if (typeof r === 'boolean') return r;
  return r?.authenticated === true;
}

export interface CreateUserInput {
  userName: string;
  password: string;
  emailAddress: string;
  fName: string;
  lName: string;
  /** Optional employer / organization. Backend requires a non-empty
   *  value, so we default to N/A when the user doesn't supply one. */
  company?: string;
  /** Force the new account into the student role for the demo. */
  studentRole?: boolean;
}

/**
 * Create a new user account. Returns the new userId on success.
 * The created user is logged in as a side-effect of the existing
 * session — we'll still call login() afterwards to make sure.
 */
export async function createUser(input: CreateUserInput): Promise<number> {
  const r = await callApi<number | string>('userApi', 'create', {
    userName: input.userName,
    password: input.password,
    confirmPassword: input.password,
    emailAddress: input.emailAddress,
    confirmEmailAddress: input.emailAddress,
    fName: input.fName,
    lName: input.lName,
    company: input.company?.trim() || 'N/A',
    studentRole: input.studentRole ?? true,
  });
  return typeof r === 'string' ? parseInt(r, 10) : r;
}

/** Returns the current user's profile if a session is established. */
export async function getCurrentUser(): Promise<UserInfo | null> {
  try {
    const r = await callApi<UserInfo | null>('userApi', 'getUserInfo');
    return r ?? null;
  } catch {
    return null;
  }
}
